import './transaction-details.scss';

import {
  fetchTransactionWebhookEvents,
  replayTransactionWebhookEvent
} from '@basqet-dashboard/common/api/transactions';
import { Button, WarningModal } from '@basqet-dashboard/common/components';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import {
  Dash,
  DetailCard,
  DetailMetric,
  DetailRow,
  MutedValue,
  StatusBadge,
  titleCase
} from './transaction-detail-primitives';
import { TransactionDetailSidebar } from './transaction-detail-sidebar';
import {
  formatAmount,
  formatDetailDate,
  formatDifference,
  formatMoney,
  getPaymentClassification,
  getTransactionStatus
} from './transaction-detail-utils';

const NOT_RECEIVED = <MutedValue>Not received</MutedValue>;

const hasAmount = (money) => money?.amount !== null && money?.amount !== undefined;

const AmountDetailsCard = ({ amounts, isAwaiting }) => {
  const classification = getPaymentClassification(amounts.payment_classification);

  return (
    <DetailCard
      title="Amount details"
      testId="amount-details"
      badge={
        classification ? (
          <StatusBadge label={classification.label} tone={classification.tone} />
        ) : null
      }>
      <div className="transaction-detail__amount-summary">
        <DetailMetric label="Expected payment" value={formatMoney(amounts.expected_payment)} />
        <DetailMetric
          label="Amount paid"
          value={hasAmount(amounts.amount_paid) ? formatMoney(amounts.amount_paid) : NOT_RECEIVED}
        />
        <DetailMetric
          label="Difference"
          value={
            hasAmount(amounts.payment_difference) ? (
              <span className={`transaction-detail__value--${classification?.tone ?? 'neutral'}`}>
                {formatDifference(amounts.payment_difference)}
              </span>
            ) : (
              <Dash />
            )
          }
        />
      </div>
      <DetailRow label="Amount initialized" value={formatMoney(amounts.initialized)} />
      <DetailRow label="Basqet fee" value={formatMoney(amounts.basqet_fee)} />
      <DetailRow
        label="Settlement amount"
        value={
          isAwaiting && !hasAmount(amounts.settlement_amount) ? (
            <MutedValue>Pending</MutedValue>
          ) : (
            formatMoney(amounts.settlement_amount)
          )
        }
      />
    </DetailCard>
  );
};

const accountStatusClassName = (status) =>
  ['ACTIVE', 'PAID'].includes(String(status).toUpperCase())
    ? 'transaction-detail__value--success'
    : 'transaction-detail__value--muted';

const VirtualAccountRows = ({ account }) => (
  <>
    <DetailRow label="Account number" value={account.account_number} copy />
    <DetailRow label="Account name" value={account.account_name} />
    <DetailRow label="Bank name" value={account.bank_name} />
    <DetailRow
      label="Expected amount"
      value={formatAmount(account.expected_amount, account.currency)}
    />
    <DetailRow
      label="Amount received"
      value={
        account.amount_received === null || account.amount_received === undefined
          ? NOT_RECEIVED
          : formatAmount(account.amount_received, account.currency)
      }
    />
    <DetailRow
      label="Account status"
      value={
        account.status ? (
          <span className={accountStatusClassName(account.status)}>
            {titleCase(account.status)}
          </span>
        ) : null
      }
    />
    <DetailRow label="Account expiry" value={formatDetailDate(account.expiry_date)} />
    <DetailRow label="Payment narration" value={account.narration} />
    <DetailRow label="Payment date" value={formatDetailDate(account.payment_date)} />
  </>
);

const CryptoRows = ({ crypto, isAwaiting }) => (
  <>
    <DetailRow label="Wallet address" value={crypto.wallet_address} copy />
    {isAwaiting && !crypto.txid ? (
      <DetailRow
        label="Transaction hash (TXID)"
        value={<MutedValue>Awaiting confirmation</MutedValue>}
      />
    ) : (
      <DetailRow label="Transaction hash (TXID)" value={crypto.txid} copy />
    )}
    <DetailRow label="Payment address expiry" value={formatDetailDate(crypto.expiry_date)} />
  </>
);

const PAYMENT_METHOD_LABELS = {
  VIRTUAL_ACCOUNT: 'Virtual account',
  CRYPTO: 'Crypto',
  NOT_AVAILABLE: 'Not available'
};

const PaymentDetailsCard = ({ payment, isAwaiting }) => {
  const method = payment?.method ?? 'NOT_AVAILABLE';

  return (
    <DetailCard
      title="Payment details"
      testId="payment-details"
      badge={<StatusBadge label={PAYMENT_METHOD_LABELS[method] ?? titleCase(method)} />}>
      {method === 'VIRTUAL_ACCOUNT' && <VirtualAccountRows account={payment.virtual_account} />}
      {method === 'CRYPTO' && <CryptoRows crypto={payment.crypto} isAwaiting={isAwaiting} />}
      {method !== 'VIRTUAL_ACCOUNT' && method !== 'CRYPTO' && (
        <MutedValue>No payment method recorded yet</MutedValue>
      )}
    </DetailCard>
  );
};

const FeeDetailsCard = ({ fees }) => (
  <DetailCard title="Fee details" testId="fee-details">
    <DetailRow label="Basqet fee" value={formatMoney(fees.basqet_fee)} />
    <DetailRow label="Payment fee bearer" value={titleCase(fees.payment_fee_bearer)} />
    <DetailRow label="Settlement fee bearer" value={titleCase(fees.settlement_fee_bearer)} />
  </DetailCard>
);

const WEBHOOK_STATUSES = {
  DELIVERED: { label: 'Delivered', tone: 'success' },
  FAILED: { label: 'Failed', tone: 'error' },
  PENDING: { label: 'Pending', tone: 'warning' },
  NOT_AVAILABLE: { label: 'Not attempted', tone: 'neutral' }
};

const httpResponseClassName = (httpStatus) => {
  if (!httpStatus) return '';
  return httpStatus < 400
    ? 'transaction-detail__value--success'
    : 'transaction-detail__value--error';
};

const WebhookDeliveryCard = ({ webhookDelivery }) => {
  const latest = webhookDelivery?.latest ?? {};
  // The API returns attempts newest first; the design numbers them oldest first.
  const attempts = [...(webhookDelivery?.attempts ?? [])].reverse();
  const badge = WEBHOOK_STATUSES[latest.status] ?? WEBHOOK_STATUSES.NOT_AVAILABLE;
  const isSuccessfulResponse = latest.http_response_code && latest.http_response_code < 400;

  return (
    <DetailCard
      title="Webhook delivery"
      testId="webhook-delivery"
      badge={<StatusBadge label={badge.label} tone={badge.tone} />}>
      <DetailRow label="Event" value={latest.event ?? <Dash />} />
      <DetailRow label="Latest attempt" value={formatDetailDate(latest.attempted_at)} />
      {/* the event's attempt count, not the array length */}
      <DetailRow label="Delivery attempts" value={latest.attempts ?? 0} />
      <DetailRow
        label="HTTP response"
        value={
          latest.http_response_code ? (
            `${latest.http_response_code}${isSuccessfulResponse ? ' OK' : ''}`
          ) : (
            <Dash />
          )
        }
        valueClassName={httpResponseClassName(latest.http_response_code)}
      />
      <div className="transaction-detail__attempts">
        {attempts.length ? (
          <>
            <p>Attempt history</p>
            {attempts.map((attempt, index) => (
              <DetailRow
                key={`${attempt.attempted_at}-${index}`}
                label={`Attempt ${index + 1}`}
                value={
                  <span className="transaction-detail__attempt-value">
                    <span>{formatDetailDate(attempt.attempted_at)?.split(', ')[1]}</span>
                    <StatusBadge
                      label={String(attempt.http_response_code ?? attempt.status)}
                      tone={attempt.http_response_code < 400 ? 'success' : 'error'}
                    />
                  </span>
                }
              />
            ))}
          </>
        ) : (
          <MutedValue>No delivery attempts yet</MutedValue>
        )}
      </div>
    </DetailCard>
  );
};

const EVENT_LABELS = {
  PAYMENT_PENDING: 'Payment pending',
  PAYMENT_RECEIVED: 'Payment received'
};

const eventLabel = (eventType) => EVENT_LABELS[eventType] ?? titleCase(eventType);

const ReplayEventModal = ({ event, transactionId, onModalClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const confirmReplay = async () => {
    setIsLoading(true);
    try {
      await replayTransactionWebhookEvent(transactionId, event.id);
      toast.success('Webhook event queued for replay');
      queryClient.invalidateQueries(['transactionWebhookEvents', transactionId]);
      queryClient.invalidateQueries(['transactionDetails', transactionId]);
      onModalClose();
    } catch (error) {
      toast.error(error?.response?.data?.message ?? 'Could not replay this webhook event');
      setIsLoading(false);
    }
  };

  return (
    <WarningModal header={{ name: 'Replay', cursive: 'webhook' }} onModalClose={onModalClose}>
      <p className="modal__text">
        Resend <strong>{eventLabel(event.event)}</strong> to the merchant&apos;s webhook URL?
      </p>
      <Button isFull variant="outline" onClick={onModalClose}>
        Cancel
      </Button>
      <br />
      <Button isFull variant="primary" onClick={confirmReplay} isLoading={isLoading}>
        Yes, replay event
      </Button>
    </WarningModal>
  );
};

ReplayEventModal.propTypes = {
  event: PropTypes.object.isRequired,
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onModalClose: PropTypes.func.isRequired
};

const WebhookEventTab = ({ event, isSelected, onSelect }) => {
  return (
    <button
      type="button"
      role="tab"
      className="transaction-detail__webhook-tab"
      aria-selected={isSelected}
      onClick={onSelect}>
      {eventLabel(event.event)}
    </button>
  );
};

WebhookEventTab.propTypes = {
  event: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired
};

const WebhookEventDetail = ({ event, transactionId }) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const badge = WEBHOOK_STATUSES[event.status] ?? WEBHOOK_STATUSES.NOT_AVAILABLE;
  const attempts = [...(event.attempts ?? [])].reverse();

  return (
    <div className="transaction-detail__webhook-event" data-testid="webhook-event">
      <div className="transaction-detail__webhook-event-header">
        <StatusBadge label={badge.label} tone={badge.tone} />
        <Button variant="outline" size="small" onClick={() => setIsConfirming(true)}>
          Replay event
        </Button>
      </div>
      <DetailRow label="Destination URL" value={event.destination_url} />
      <DetailRow label="Delivery attempts" value={event.attempt_count ?? 0} />
      {event.delivered_at && (
        <DetailRow label="Delivered at" value={formatDetailDate(event.delivered_at)} />
      )}
      {event.failed_at && <DetailRow label="Failed at" value={formatDetailDate(event.failed_at)} />}
      {attempts.length > 0 && (
        <div className="transaction-detail__attempts">
          <p>Attempt history</p>
          {attempts.map((attempt) => {
            const attemptBadge = attempt.http_response_code
              ? {
                  label: String(attempt.http_response_code),
                  tone: attempt.http_response_code < 400 ? 'success' : 'error'
                }
              : WEBHOOK_STATUSES[attempt.status] ?? WEBHOOK_STATUSES.NOT_AVAILABLE;

            return (
              <DetailRow
                key={attempt.attempt_number}
                label={`Attempt ${attempt.attempt_number}`}
                value={
                  <span className="transaction-detail__attempt-value">
                    <span>{formatDetailDate(attempt.attempted_at)}</span>
                    <StatusBadge
                      label={attemptBadge.label}
                      tone={attemptBadge.tone}
                      title={attempt.error_message || undefined}
                    />
                  </span>
                }
              />
            );
          })}
        </div>
      )}
      {isConfirming && (
        <ReplayEventModal
          event={event}
          transactionId={transactionId}
          onModalClose={() => setIsConfirming(false)}
        />
      )}
    </div>
  );
};

WebhookEventDetail.propTypes = {
  event: PropTypes.object.isRequired,
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

const WebhookEventsCard = ({ transactionId }) => {
  const [selectedEventId, setSelectedEventId] = useState(null);
  const { data, isLoading, isError } = useQuery(
    ['transactionWebhookEvents', transactionId],
    () => fetchTransactionWebhookEvents(transactionId),
    { enabled: Boolean(transactionId) }
  );

  const events = data?.data?.events ?? [];
  const selectedEvent = events.find((event) => event.id === selectedEventId) ?? events[0] ?? null;

  return (
    <DetailCard title="Webhook events" testId="webhook-events">
      {isLoading && <MutedValue>Loading webhook events…</MutedValue>}
      {isError && <MutedValue>Could not load webhook events</MutedValue>}
      {!isLoading && !isError && events.length === 0 && (
        <MutedValue>No webhook events for this transaction yet</MutedValue>
      )}
      {events.length > 0 && (
        <>
          <div className="transaction-detail__webhook-tabs" role="tablist">
            {events.map((event) => (
              <WebhookEventTab
                key={event.id}
                event={event}
                isSelected={event.id === selectedEvent.id}
                onSelect={() => setSelectedEventId(event.id)}
              />
            ))}
          </div>
          <WebhookEventDetail event={selectedEvent} transactionId={transactionId} />
        </>
      )}
    </DetailCard>
  );
};

WebhookEventsCard.propTypes = {
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const TransactionDetails = ({ details, transactionId }) => {
  const { summary, amounts, customer, payment, fees, timeline } = details;
  const webhookDelivery = details.webhook_delivery;
  const status = getTransactionStatus(summary.status);
  const isAwaiting = status.label === 'Awaiting payment';

  return (
    <main className="transaction-detail">
      <header className="transaction-detail__hero">
        <div>
          <h1>
            Transaction <em>details</em>
          </h1>
          <p>
            {summary.description || 'No description'} • {summary.basqet_reference}
          </p>
        </div>
        <StatusBadge label={status.label} tone={status.tone} />
      </header>

      <div className="transaction-detail__layout">
        <div className="transaction-detail__primary">
          <AmountDetailsCard amounts={amounts} isAwaiting={isAwaiting} />
          <PaymentDetailsCard payment={payment} isAwaiting={isAwaiting} />
          <FeeDetailsCard fees={fees} />
          <WebhookDeliveryCard webhookDelivery={webhookDelivery} />
          <WebhookEventsCard transactionId={transactionId} />
        </div>

        <TransactionDetailSidebar
          summary={summary}
          customer={customer}
          timeline={timeline}
          status={status}
        />
      </div>
    </main>
  );
};

const moneyShape = PropTypes.shape({
  amount: PropTypes.number,
  currency: PropTypes.string
});

AmountDetailsCard.propTypes = {
  amounts: PropTypes.object.isRequired,
  isAwaiting: PropTypes.bool.isRequired
};
VirtualAccountRows.propTypes = { account: PropTypes.object.isRequired };
CryptoRows.propTypes = {
  crypto: PropTypes.object.isRequired,
  isAwaiting: PropTypes.bool.isRequired
};
PaymentDetailsCard.propTypes = {
  payment: PropTypes.object,
  isAwaiting: PropTypes.bool.isRequired
};
FeeDetailsCard.propTypes = {
  fees: PropTypes.shape({
    basqet_fee: moneyShape,
    payment_fee_bearer: PropTypes.string,
    settlement_fee_bearer: PropTypes.string
  }).isRequired
};
WebhookDeliveryCard.propTypes = { webhookDelivery: PropTypes.object };
TransactionDetails.propTypes = {
  details: PropTypes.object.isRequired,
  transactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export { TransactionDetails };
